import { useRef } from 'react';
import { singletonHook } from 'react-singleton-hook';
import useArtifactInfoService from '../artifactInfoService';
import type { ArtifactsInfoResponse } from '../types';
import { filterValidArtifactIds } from './createArtifactsInfoReceiver';

type BudgetArtifactsInfoReceiver = {
  hookLoading: boolean;
  collectBudgetArtifactsData: (
    budgetId: StringOrNumber,
    initialAttachmentsIds: string[],
  ) => Promise<ArtifactsInfoResponse['artifacts']>;
};

const initState: BudgetArtifactsInfoReceiver = {
  hookLoading: true,
  collectBudgetArtifactsData: async () => ({}),
};

type BudgetBatch = {
  ids: Set<string>;
  callers: Array<{
    resolve: (artifacts: ArtifactsInfoResponse['artifacts']) => void;
    reject: (error: unknown) => void;
  }>;
};

const useBudgetArtifactsInfoReceiverImpl = () => {
  const { getBudgetArtifactsInfo } = useArtifactInfoService();
  const batches = useRef(new Map<string, BudgetBatch>());

  const collectBudgetArtifactsData: BudgetArtifactsInfoReceiver['collectBudgetArtifactsData'] = async (
    budgetId,
    initialAttachmentsIds,
  ) => {
    const ids = filterValidArtifactIds(initialAttachmentsIds);
    if (!budgetId || !ids.length) return {};

    const key = String(budgetId);
    let batch = batches.current.get(key);
    if (!batch) {
      batch = { ids: new Set(), callers: [] };
      batches.current.set(key, batch);
      const pendingBatch = batch;
      setTimeout(async () => {
        // New callers form a separate batch while this request is in flight.
        batches.current.delete(key);
        try {
          const response = await getBudgetArtifactsInfo(key, [...pendingBatch.ids].join(','));
          pendingBatch.callers.forEach(caller => caller.resolve(response?.artifacts ?? {}));
        } catch (error) {
          pendingBatch.callers.forEach(caller => caller.reject(error));
        }
      }, 100);
    }

    ids.forEach(id => batch.ids.add(id));
    return new Promise((resolve, reject) => batch.callers.push({ resolve, reject }));
  };

  return {
    hookLoading: false,
    collectBudgetArtifactsData,
  };
};

const useBudgetArtifactsInfoReceiver = singletonHook(initState, useBudgetArtifactsInfoReceiverImpl);

export default useBudgetArtifactsInfoReceiver;
